<template>
  <div class="d-flex">
    <ui-radio-group
      class="wrapper"
      :class="maximumCostAvailable ? 'w45' : 'w50'"
      v-model="costModel"
      @change="onChangeCostModel"
      :label="t('Cost model')"
      :disabled="disabled"
    >
      <ui-radio value="auto">
        <template #label>
          <div>
            {{ t('Auto') }}
            <ui-tooltip-icon
              preset="help"
              class="mb-2"
              :text="t('Auto should be selected if you have defined the cost parameter in your traffic source settings.')"
            ></ui-tooltip-icon>
          </div>
        </template>
      </ui-radio>
      <ui-radio value="autoCpm">
        <template #label>
          <div>
            {{ t('Auto CPM') }}
            <ui-tooltip-icon
              preset="help"
              class="mb-2"
              :text="t('Select this model if your traffic source passes the traffic cost of 1000 visits in the dedicated cost macro.')"
            ></ui-tooltip-icon>
          </div>
        </template>
      </ui-radio>
      <ui-radio value="cpv">
        <template #label>
          <div>
            {{ t('CPV') }}
            <ui-tooltip-icon
              preset="help"
              class="mb-2"
              :text="t('CPV allows you to define cost per visit.')"
            ></ui-tooltip-icon>
          </div>
        </template>
      </ui-radio>
      <ui-radio value="cpm">
        <template #label>
          <div>
            {{ t('CPM') }}
            <ui-tooltip-icon
              preset="help"
              class="mb-2"
              :text="t('CPM allows you to introduce cost per 1,000 impressions.')"
            ></ui-tooltip-icon>
          </div>
        </template>
      </ui-radio>
      <ui-radio value="cpa">
        <template #label>
          <div>
            {{ t('CPA') }}
            <ui-tooltip-icon
              preset="help"
              class="mb-2"
              :text="t('CPA allows you to enter cost per conversion.')"
            ></ui-tooltip-icon>
          </div>
        </template>
      </ui-radio>
      <ui-radio value="revShare">
        <template #label>
          <div>
            {{ t('RevShare') }}
            <ui-tooltip-icon
              preset="help"
              class="mb-2"
              :text="t('RevShare indicates the percent of revenue that you will pay to your traffic source.')"
            ></ui-tooltip-icon>
          </div>
        </template>
      </ui-radio>
      <ui-radio value="none">
        <template #label>
          <div>
            {{ t('Do not track') }}
            <ui-tooltip-icon preset="help" class="mb-2">
              <span>{{ t('Select "Do Not Track" if you are not sure what cost option should be selected. Cost can be specified later via Update cost option.') }}</span>
            </ui-tooltip-icon>
          </div>
        </template>
      </ui-radio>
    </ui-radio-group>
    <div v-if="maximumCostAvailable && isAutomaticCost" class="w55">
      <ui-checkbox
        v-model="maxCostEnabled"
        :disabled="disabled"
        :label="t('Maximum CPC protection')"
        :help-tooltip="t('You can set the maximum allowed cost per click to filter anomalous costs.<br>The limit is always configured in USD, regardless of your account or traffic currency.')"
      />
      <template v-if="maxCostEnabled">
        <ui-input
          v-model="maxCostUsd"
          :disabled="disabled"
          :label="t('Max CPC')"
          prefix="$"
          format="0.000000"
          :min="0"
          :hide-details="2"
          :error-messages="maxCostErrors"
        />
        <ui-select
          v-model="maxCostAction"
          :disabled="disabled"
          :label="t('When CPC is Higher')"
          :items="maxCostActions"
          item-text="name"
          item-value="value"
          :return-object="false"
        />
        <ui-select
          v-if="maxCostAction === 0"
          v-model="maxCostBotOfferId"
          :disabled="disabled"
          :label="t('Redirect Bot to Offer')"
          :items="offerOptions"
          :fetchOptions="fetchOfferOptions"
          item-text="nameFull"
          item-value="id"
          :return-object="false"
          :hide-details="2"
          :loading="!store.cache.offers.isUploaded"
          :error-messages="maxCostBotOfferErrors"
        />
      </template>
    </div>
    <div
      class="w50"
      v-if="costModel === 'cpv' || costModel === 'cpm' || costModel === 'cpa' || costModel === 'revShare'"
    >
      <ui-input
        v-model="costValue"
        :disabled="disabled"
        :label="costValueLabel"
        :prefix="iconCurrency"
        :suffix="iconCurrencyRevshare"
        :max="costMaxValue"
        :format="costFormat"
        :min="costMinValue"
        :has-errors="!!errors.length"
      ></ui-input>
      <div v-if="costModel === 'cpm'" class="tracking_domain-notice mt-14">
        {{ t('Only applicable with ‘Track Impressions’ option') }}
      </div>
      <div v-if="costModel === 'cpa' || costModel === 'revShare'" class="tracking_domain-notice mt-14">
        {{ t('Conversion cost could be passed to traffic source postback via {0} token', ['{campaignCpa}']) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import currencySigns from '@/components/helpers/currency-list';

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    errors?: string[];
    currencyId?: string;
    maximumCostAvailable?: boolean;
    maxCostErrors?: string[];
    maxCostBotOfferErrors?: string[];
    offerOptions?: Array<{ id: string; nameFull?: string }>;
    fetchOfferOptions?: (opts: { search: string }) => Promise<unknown[]>;
  }>(),
  {
    disabled: false,
    errors: () => [],
    currencyId: 'USD',
    maximumCostAvailable: false,
    maxCostErrors: () => [],
    maxCostBotOfferErrors: () => [],
    offerOptions: () => [],
  },
);

const costModel = defineModel<string>('costModel', { default: 'auto' });
const costValue = defineModel<number>('costValue', { default: 0 });
const maxCostEnabled = defineModel<boolean>('maxCostEnabled', { default: false });
const maxCostUsd = defineModel<number | null>('maxCostUsd', { default: null });
const maxCostAction = defineModel<0 | 1>('maxCostAction', { default: 1 });
const maxCostBotOfferId = defineModel<string | null>('maxCostBotOfferId', { default: null });

const { t } = useI18n();
const store = useStore();

const isAutomaticCost = computed(() => ['auto', 'autoCpm'].includes(costModel.value));

const maxCostActions = computed(() => [
  {
    value: 0,
    name: t('Record as Bot With Bot Cost'),
    ...(!store.user.enableBotFilters && { planLimits: 'botFiltersAccess' }),
  },
  { value: 1, name: t('Don\'t Record Click and Cost') },
]);

const onChangeCostModel = () => {
  costValue.value = 0;
  if (!isAutomaticCost.value) {
    maxCostEnabled.value = false;
  }
};

watch(maxCostEnabled, (enabled) => {
  if (enabled) return;
  maxCostUsd.value = null;
  maxCostAction.value = 1;
  maxCostBotOfferId.value = null;
});

watch(maxCostAction, (action) => {
  if (action !== 0) {
    maxCostBotOfferId.value = null;
  }
});

const costValueTitles = computed(() => ({
  cpv: t('CPV cost'),
  cpm: t('CPM cost'),
  cpa: t('CPA cost'),
  revShare: t('RevShare cost, %'),
}));

const costValueLabel = computed(
  () => costValueTitles.value[costModel.value as keyof typeof costValueTitles.value],
);

const isRevShare = computed(() => costModel.value === 'revShare');

const costFormat = computed(() => (isRevShare.value ? null : '0.000000'));

const costMaxValue = computed(() => (isRevShare.value ? 100 : null));

const costMinValue = computed(() => (isRevShare.value ? 0 : 0.000001));

const iconCurrency = computed(() => (isRevShare.value ? '' : currencySigns[props.currencyId]));

const iconCurrencyRevshare = computed(() => (isRevShare.value ? '%' : ''));
</script>

<style lang="scss" scoped>
.wrapper {
 .ui-icon {
   pointer-events: all;
 }
}

.cost-model {
  margin-top: 0;
  padding-top: 0;
}
</style>
